import {
  logger,
  useDfwNavigation,
  useLayoutStore,
  useMcmNavigation,
  useRptNavigation,
  useSfNavigation,
} from '@brand-console/utilities'
import { useEnabledServices } from '@cart/ui/hooks/useEnabledServices'
import { useCurrentContext } from '@cartdotcom/auth'
import { useMemo } from 'react'

import { MenuItems } from '../AppsDropdown.constants'

export const useEnabledItems = (log: typeof logger) => {
  const { product, sfAccount } = useLayoutStore()
  const { currentOrganization, currentBusiness } = useCurrentContext()
  const {
    enabled: { mcmEnabled, sfEnabled, dfwEnabled, ffEnabled, rptEnabled },
  } = useEnabledServices(currentOrganization?.id, currentBusiness?.id, log)
  const mcmNavigation = useMcmNavigation()
  const dfwNavigation = useDfwNavigation()
  const sfNavigation = useSfNavigation(sfAccount)
  const rptNavigation = useRptNavigation()

  type EnabledMenuItems = { [key in keyof typeof MenuItems]: boolean }
  const enabledKeys: EnabledMenuItems = useMemo(
    () => ({
      dfw: dfwEnabled,
      ff: ffEnabled,
      mcm: mcmEnabled,
      sf: sfEnabled,
      rpt: rptEnabled,
    }),
    [dfwEnabled, ffEnabled, mcmEnabled, sfEnabled, rptEnabled],
  )

  const enabledNavigation = useMemo(
    () => ({
      dfw: { link: dfwNavigation.link },
      mcm: { link: mcmNavigation.link },
      sf: { link: sfNavigation.link, onClick: sfNavigation.onClick },
      rpt: { link: rptNavigation.link },
    }),
    [
      dfwNavigation.link,
      mcmNavigation.link,
      sfNavigation.link,
      sfNavigation.onClick,
      rptNavigation.link,
    ],
  )

  const enabledItems = useMemo(() => {
    return Object.keys(enabledKeys)
      .filter((key) => key !== product && enabledKeys[key])
      .map((key) => {
        const enabledItem = { ...MenuItems[key] }
        const navigation = enabledNavigation[key]

        if (navigation?.link) enabledItem.link = navigation.link
        else if (navigation?.onClick) {
          enabledItem.link = '#'
          enabledItem.onClick = navigation.onClick
        }

        return enabledItem
      })
  }, [enabledKeys, enabledNavigation, product])

  const disabledItems = useMemo(() => {
    return Object.keys(enabledKeys)
      .filter((key) => key !== product && !enabledKeys[key])
      .map((key) => MenuItems[key])
  }, [enabledKeys, product])

  return [enabledItems, disabledItems] as const
}
